import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { deleteProject } from '@/data/projectApi'
import { useProjects, type ProjectRecord } from '@/data/useProjects'
import { useUsers } from '@/data/useUsers'
import { errorMessage } from '@/lib/errors'
import { ProjectFormModal } from './ProjectFormModal'
import { ProjectTable } from './ProjectTable'

type Banner = { type: 'success' | 'error'; text: string } | null

export function ProjectsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { projects, loading, error } = useProjects()
  const { users } = useUsers()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ProjectRecord | null>(null)
  const [pendingDelete, setPendingDelete] = useState<ProjectRecord | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [banner, setBanner] = useState<Banner>(null)

  const suppliers = useMemo(() => users.filter((u) => u.role === 'supplier'), [users])
  const clients = useMemo(() => users.filter((u) => u.role === 'client'), [users])

  function openCreate() {
    setEditing(null)
    setFormOpen(true)
  }

  function openEdit(project: ProjectRecord) {
    setEditing(project)
    setFormOpen(true)
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) {
      return
    }
    setDeleting(true)
    try {
      await deleteProject(pendingDelete.id)
      setBanner({ type: 'success', text: `${pendingDelete.name} deleted.` })
      setPendingDelete(null)
    } catch (err) {
      setBanner({ type: 'error', text: errorMessage(err) })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-800">Projects</h1>
        <Button onClick={openCreate}>Add project</Button>
      </div>

      {banner && (
        <div
          className={`mb-4 rounded-lg px-4 py-2 text-sm ${
            banner.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {banner.text}
        </div>
      )}

      {loading && <p className="text-slate-500">Loading projects…</p>}
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
      {!loading && !error && (
        <ProjectTable
          projects={projects}
          onOpen={(project) => navigate(project.id)}
          onEdit={openEdit}
          onDelete={setPendingDelete}
        />
      )}

      {formOpen && (
        <ProjectFormModal
          project={editing}
          suppliers={suppliers}
          clients={clients}
          createdBy={user?.uid ?? ''}
          onClose={() => setFormOpen(false)}
          onSaved={(name) => {
            setFormOpen(false)
            setBanner({ type: 'success', text: `${name} ${editing ? 'updated' : 'created'}.` })
          }}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete project"
        message={pendingDelete ? `Delete "${pendingDelete.name}"? This cannot be undone.` : ''}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}
